import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  useTheme,
  useMediaQuery,
  Skeleton,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { 
  fetchPatientInvoices, 
  fetchInvoiceDetail, 
  downloadInvoicePDF,
  clearCurrentInvoice 
} from '../../features/billing/patientBillingSlice';
import { useFeatureFlag } from '../../config/featureFlags';

const Billing = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  
  // Feature flags
  const isBillingEnabled = useFeatureFlag('FEATURE_PATIENT_BILLING');
  
  // Redux state
  const { invoices, currentInvoice, downloading, downloadError } = useSelector((state) => state.patientBilling);
  
  // Local state
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchPatientInvoices({ page, status: statusFilter }));
  }, [dispatch, page, statusFilter]);

  useEffect(() => {
    if (downloadError) {
      toast.error(downloadError);
    }
  }, [downloadError]);

  const handleViewDetails = async (invoiceId) => {
    try {
      await dispatch(fetchInvoiceDetail(invoiceId)).unwrap();
      setSelectedInvoice(invoiceId);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load invoice details');
    }
  };

  const handleDownloadPDF = async (invoiceId) => {
    try {
      await dispatch(downloadInvoicePDF(invoiceId)).unwrap();
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedInvoice(null);
    dispatch(clearCurrentInvoice());
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'paid':
        return 'Paid';
      case 'pending':
        return 'Pending';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  if (!isBillingEnabled) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Billing feature is currently not available.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Billing & Invoices
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Invoices
                  </Typography>
                  <Typography variant="h4">
                    {invoices.pagination?.count || 0}
                  </Typography>
                </Box>
                <ReceiptIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Paid
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {invoices.data.filter(inv => inv.status === 'paid').length}
                  </Typography>
                </Box>
                <PaymentIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Pending
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {invoices.data.filter(inv => inv.status === 'pending').length}
                  </Typography>
                </Box>
                <ScheduleIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Amount
                  </Typography>
                  <Typography variant="h4">
                    {formatCurrency(invoices.data.reduce((sum, inv) => sum + inv.amount, 0))}
                  </Typography>
                </Box>
                <PaymentIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Invoices Table */}
      <Card>
        <CardContent>
          {invoices.loading ? (
            <Box sx={{ p: 2 }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} variant="rectangular" height={60} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : invoices.error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {invoices.error}
            </Alert>
          ) : invoices.data.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="textSecondary">
                No invoices found
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice ID</TableCell>
                      <TableCell>Doctor</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.data.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell>{invoice.id}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {invoice.doctor.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {invoice.doctor.specialization}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {format(new Date(invoice.consultation_date), 'MMM dd, yyyy')}
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="medium">
                            {formatCurrency(invoice.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(invoice.status)}
                            color={getStatusColor(invoice.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() => handleViewDetails(invoice.id)}
                            >
                              View
                            </Button>
                            <Button
                              size="small"
                              startIcon={<DownloadIcon />}
                              onClick={() => handleDownloadPDF(invoice.id)}
                              disabled={downloading}
                            >
                              PDF
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {invoices.pagination && invoices.pagination.count > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={Math.ceil(invoices.pagination.count / (invoices.pagination.page_size || 10))}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Invoice Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Invoice Details
          {currentInvoice?.data && (
            <Typography variant="body2" color="textSecondary">
              Invoice #{currentInvoice.data.id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {currentInvoice?.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : currentInvoice?.error ? (
            <Alert severity="error">
              {currentInvoice.error}
            </Alert>
          ) : currentInvoice?.data ? (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Doctor Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" fontWeight="medium">
                      {currentInvoice.data.doctor.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {currentInvoice.data.doctor.specialization}
                    </Typography>
                    {currentInvoice.data.doctor.clinic && (
                      <Typography variant="body2" color="textSecondary">
                        {currentInvoice.data.doctor.clinic}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Patient Information
                  </Typography>
                  <Typography variant="body1">
                    {currentInvoice.data.patient_name}
                  </Typography>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Invoice Details
                  </Typography>
                  <Box>
                    <Typography variant="body2">
                      <strong>Consultation Date:</strong> {format(new Date(currentInvoice.data.consultation_date), 'MMMM dd, yyyy')}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Amount:</strong> {formatCurrency(currentInvoice.data.amount)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Status:</strong> 
                      <Chip
                        label={getStatusLabel(currentInvoice.data.status)}
                        color={getStatusColor(currentInvoice.data.status)}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                    <Typography variant="body2">
                      <strong>Created:</strong> {format(new Date(currentInvoice.data.created_at), 'MMMM dd, yyyy HH:mm')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Payment Information
                  </Typography>
                  <Box>
                    <Typography variant="body2">
                      <strong>Payment Method:</strong> {currentInvoice.data.payment_method || 'Not specified'}
                    </Typography>
                    {currentInvoice.data.transaction_id && (
                      <Typography variant="body2">
                        <strong>Transaction ID:</strong> {currentInvoice.data.transaction_id}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailDialog}>Close</Button>
          {currentInvoice?.data && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownloadPDF(currentInvoice.data.id)}
              disabled={downloading}
            >
              Download PDF
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Billing;
